import { getIsTrackingEnabled } from './getIsTrackingEnabled';
import { getApiVariables, getEnrichedData } from './trackingDetails';
import {
  RudderstackApiRequest,
  TrackEventProperties,
  TrackPageViewProperties,
} from './types';
import { TRACK_REQUEST_TIMEOUT } from 'lib/helpers/constants';
import { isAnalyticsConsentGiven } from 'lib/helpers/isAnalyticsConsentGiven';

interface PostDataProperties {
  data: RudderstackApiRequest;
  /**
   * In some cases, we have to wait for the request to complete before proceeding (e.g.: redirects),
   * but we don't want it to hang indefinitely and block the UI.
   * Set up a timeout for the request to prevent it from hanging indefinitely.
   * If `limitTimeout` is true, the request will be aborted after `TRACK_REQUEST_TIMEOUT` milliseconds.
   */
  limitTimeout?: boolean;
}

const postData = async ({ data, limitTimeout }: PostDataProperties) => {
  const { anonymousId, sentAt, type } = data;

  const { trackEventUrl, trackPageUrl, writeKey } = getApiVariables();

  const isTrackingEnabled = getIsTrackingEnabled();
  if (!isTrackingEnabled) return;

  const url = type === 'page' ? trackPageUrl : trackEventUrl;

  const controller = new AbortController();

  try {
    const response = await fetch(url, {
      body: JSON.stringify(data),
      headers: {
        Authorization: `Basic ${writeKey}`,
        'Content-Type': 'application/json',
        'WWW-Authenticate': 'Basic',
        anonymousId,
        sentAt,
      },
      method: 'POST',
      signal: limitTimeout ? controller.signal : undefined,
    });

    if (limitTimeout) {
      const id = setTimeout(() => controller.abort(), TRACK_REQUEST_TIMEOUT);

      clearTimeout(id);
    }

    if (!response.ok) {
      throw new Error(`Response status: ${response.status}`);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error tracking event:', error);
  }
};

export const trackEvent = async (
  eventData: TrackEventProperties,
  limitTimeout?: boolean,
) => {
  if (!isAnalyticsConsentGiven()) return;

  const data: RudderstackApiRequest = {
    ...getEnrichedData(eventData),
    event: 'event',
    type: 'track',
  };
  await postData({ data, limitTimeout });
};

export const trackPageView = async (
  pageViewData: TrackPageViewProperties,
  limitTimeout?: boolean,
) => {
  if (!isAnalyticsConsentGiven()) return;

  const data: RudderstackApiRequest = {
    ...getEnrichedData(pageViewData),
    event: 'page',
    type: 'page',
  };
  await postData({ data, limitTimeout });
};
